import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import Markdown from 'react-markdown';
import { flushSync } from "react-dom";
import { styled, useTheme } from '@mui/material/styles';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import { References } from './bots.references';
import { Grid, TextField, Button, SvgIcon, Tooltip, Typography, Card, Box, Stack, ButtonGroup, Chip, Avatar } from '@mui/material';
import SearchMdIcon from '@mui/icons-material/Search';
import StopRounded from '@mui/icons-material/StopRounded';
import CloseIcon from '@mui/icons-material/Close'; // Import Close icon
import IconButton from '@mui/material/IconButton'; // Import IconButton component
import { PiTextAlignLeftLight, PiBook } from 'react-icons/pi';
import { SourcesList } from './sources-list';
import { AiOutlineCheck, AiOutlineCopy, AiOutlineGlobal } from 'react-icons/ai';
import { citationToHighlightUrl } from './navigateToDocument';
import { sendToContentScript } from "@plasmohq/messaging"
import { useStorage } from "@plasmohq/storage/hook"
import { Storage } from "@plasmohq/storage"
import Snackbar from '@mui/material/Snackbar';
import debounce from 'lodash/debounce';
import axios from 'axios'
const storage = new Storage()

import './bots.css';
const MarkdownWrapper = styled('div')(({ theme }) => ({
    // ... (rest of the styles)
}));

const Spacer = ({ height }) => <Box sx={{ height }} />;

const mailTo = () => {
    var formattedBody = document.getElementById('answer-renderer').innerText;
    var mailToLink = "mailto:x@y.com?body=" + encodeURIComponent(formattedBody);
    window.location.href = mailToLink;
}

const getVersion = () => {
    const query = new URLSearchParams(window.location.search);
    let v = query.get('version')
    const activeVersions = [2]
    if (activeVersions.includes(parseInt(v))) {
        return v
    }
    else return ""
}

const handleClose = () => {
    window?.xprops?.closeChat(); // Close the widget
};

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate"
                    {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2"
                    color="text.secondary">{`${Math.round(
                        props.value,
                    )}%`}</Typography>
            </Box>
        </Box>
    );
}


const setResponseDataStoreValue = (responseData) => {
    storage.set('responseData', responseData)
    return 'stored'
}

const getResponseData = async (query) => {
    return await storage.get('responseData')
}

const DrugBubble = ({ name }) => (
    <span style={{
        backgroundColor: '#e0f7fa',
        borderRadius: '16px',
        padding: '2px 8px',
        margin: '0 2px',
        display: 'inline-block',
        fontSize: '0.6em',
    }}>
        {name}
    </span>
);

export const Main = (props) => {
    const [question, setQuestion, {
        setRenderValue,
        setStoreValue,
        remove
    }] = useStorage("question")
    const [progress, setProgress] = useState(0);
    const [startTime, setStartTime] = useState(() => Date.now());
    const [showOverloadMessage, setShowOverloadMessage] = useState(false);
    const [asephaSettings, setAsephaSettings] = useState(() => props.asephaSettings || {});
    const theme = useTheme();
    const [responseData, setResponseData] = useState({ response: '', documents: [], citations: [], chunks: [] });

    const [responseLoading, setResponseLoading] = useState(false);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [showSearchBar, setShowSearchBar] = useState(() => props.showSearchBar);
    const [tempQuestion, setTempQuestion] = useState('');
    const [activeSource, setActiveSource] = useState(null);
    const UI_VERSION = useMemo(() => props?.asephaSettings?.version ?? getVersion() ?? '', [props.asephaSettings]);
    const [disclaimerShow, setDisclaimerShow] = useState(true)
    const customerId = props?.customerId
    const disclaimerText = 'This Clinical Search feature is in beta form and under active development - please use with discretion.'
    const inputRef = useRef()
    const [botError, setBotError] = useState({ message: '', open: false })
    const user = props?.user;

    const [highlightedDrugs, setHighlightedDrugs] = useState([]);

    const highlightDrugs = useCallback(
        debounce(async (text) => {
            if (text && text.length > 0) {
                const drugs = await extractDrugsFromLibrary(text);
                if (drugs && drugs.length > 0) {
                    setHighlightedDrugs(drugs);
                } else {
                    setHighlightedDrugs([]);
                }
            } else {
                setHighlightedDrugs([]);
            }
        }, 500),
        []
    );

    useEffect(() => {
        highlightDrugs(question);
    }, [question, highlightDrugs]);


    async function extractDrugsFromLibrary(input) {
        const url = process.env.REACT_APP_BACKEND_ENDPOINT + '/api/utilities/find-drugs';
        const payload = { query: input };
        const headers = {
          "Accept": "application/json",
          "Content-Type": "application/json"
        }
        try {
          const startTime = Date.now();
          const response = await axios.post(url, payload, { headers: headers });
          const endTime = Date.now();
          const matchedDrugs = response.data;
          return matchedDrugs;
        } catch (error) {
          console.error("Error extracting drugs from library:", error);
        }
        return null;
    }

  

    const searchTypes = {
        'all': {
            text: 'All',
            avatar: <Avatar alt="all"><AiOutlineGlobal /></Avatar>
        },
        'page': {
            text: 'This Page',
            avatar: <Avatar alt="page"></Avatar>
        }
    }

    const [searchType, setSearchType] = useState(searchTypes['all'])
    const backendEndpoint = props.backendEndpoint
    const rendererEndpoint = props.rendererEndpoint
    const extension = props.extension;

    const changeSearchType = async (url) => {
        try {
            if (searchType.text === 'All') {
                let context_url = url || await sendToContentScript({ name: 'getUrl' }) || undefined
                if (context_url) {
                    const favicon = `https://www.google.com/s2/favicons?sz=64&domain=${context_url}`;
                    setSearchType({ text: 'This Page', avatar: <Avatar alt="page" src={favicon} sx={{ width: 20, height: 20 }} /> })
                } else {
                    setSearchType(searchTypes['page'])
                }
            }
            else setSearchType(searchTypes['all'])
        } catch (error) {
            console.error("Error changing search type: ", error);
        }
    }

    const position = useRef({
        beforeStart: 0,
        beforeEnd: 0
    });
    const easeOutFunction = (t) => {
        return 1 - (1 - t) * (1 - t) * (1 - t);  // Cubic ease-out
    };

    const handleQuestionChange = ((e) => {
        const beforeStart = e.target.selectionStart;
        const beforeEnd = e.target.selectionEnd;
        position.current = {
            beforeStart,
            beforeEnd
        };
        flushSync(() => {
            setQuestion(e.target.value);
            setRenderValue(e.target.value);
        });
        inputRef.current.setSelectionRange(beforeStart, beforeEnd);
    })

    const requestIntelligentSearch = async (query, context_url = null) => {
        const rootUrl = backendEndpoint
        const response = await fetch(`${rootUrl}/api/agents/intelligent-search`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                'query': !query ? '' : query,
                'stream': true,
                'language': 'en',
                'documentsOnly': false,
                'user': user?.uid || customerId || null,
                'payload': { 'domain': window.location.hostname, context_url: context_url || null }
            }),
        });
        const resJson = await response.json();
        return resJson;
    };


    const handleSearch = async (query, setResponseData, setResponseLoading, setDocumentsLoading, setProgress, progress, activeSource, setActiveSource, setStartTime, setResponseDataStoreValue, setBotError) => {
        if (query == '' || query == null || query == undefined) return;
        try {
            let context_url = null;

            try {
                if (extension) {
                    context_url = await sendToContentScript({ name: 'getUrl' }) || null
                }
            } catch (err) {
                console.log(err)
            }
            setTempQuestion(query);
            setResponseData({ response: '', documents: [] });
            setStartTime(Date.now())

            if (activeSource) {
                await unsubscribeFromSSE(activeSource, setActiveSource);
            }

            setResponseLoading(true);
            setDocumentsLoading(true);
            setProgress(0);

            const taskId = await requestIntelligentSearch(query, searchType.text === 'This Page' ? context_url : '');
            const payload = { query, context_url, taskId }

            let source = await subscribeToSSE(taskId, setResponseData, setResponseLoading, setDocumentsLoading, setProgress, progress, setResponseDataStoreValue, payload, setBotError);
            setActiveSource(source);
            setQuestion('')
            setStoreValue()
        }
        catch (error) {
            console.log(error);
        }
    };

    const isNotSameId = (event, UNIQUE_ID) => {
        return event.lastEventId != UNIQUE_ID;
    };
    const processToken = (token) => {
        return token.replace(/\\n/g, '\n').replace(/\"/g, '')
    };

    const unsubscribeFromSSE = async (source) => {
        if (source) source.close();
        setActiveSource(null);
    };

    const openUrl = (url) => {
        window.open(url, '_blank');
    }

    const buildCitationMap = (citation_id_brackat, newBody, citations, chunks, chunkId, setResponseData) => {
        let citationMap = { id: 1, id_brackat: '', insert: '' }
        citationMap.id = parseInt(citation_id_brackat.replaceAll('[', '').replaceAll(']', ''));
        citationMap.id_brackat = citation_id_brackat;
        let targetCitation = citations?.find((citation) => citation.document_id === citationMap.id);
        if (!targetCitation) {
            citationMap.insert = citationMap.id_brackat
            return newBody.replaceAll(citationMap.id_brackat, '');
        }
        targetCitation.text = chunks.find(x => x.source_id === citationMap.id && chunkId.includes(x.chunk_id))?.pageContent || '';
        const { url, highlightEncoded } = citationToHighlightUrl(targetCitation, false, true)
        const endpoint = props.rendererEndpoint || rendererEndpoint

        // Increment the targetCitation source if reused
        if (!targetCitation.usageCount) {
            targetCitation.usageCount = 1;
        } else {
            targetCitation.usageCount += 1;
        }

        const newHighlightUrl = `${endpoint}/?url=${url}&highlight=${highlightEncoded}`
        citationMap.insert = ` <a href="${newHighlightUrl}" target="_blank" style="display: inline-block; border-radius: 50%; width: 15px; height: 15px; background-color: grey; text-align: center; color: white; text-decoration: none; font-size: 10px; vertical-align: top;">${targetCitation.source_id}</a>`;
        newBody = newBody.replaceAll(citationMap.id_brackat, citationMap.insert);
        if (setResponseData) {
            setResponseData(data => ({
                ...data,
                documents: data.documents.map(x => {
                    if (x.source_id && targetCitation.source_id)
                        if (x.source_id == targetCitation.source_id) {
                            x['inlineUsed'] = true
                        }
                    if (!x.source_id) {
                    let findIndex = citations?.findIndex((citation) => citation.url == x.url)
                    x['source_id'] = citations[findIndex]?.source_id
                    }
                    return x
                })
            }))
        }
        return newBody
    }
    const buildInlineCitations = (newBody, citations, chunks, setResponseData) => {
        const regex = /\[(\d+):(\d+)(?:-(\d+))?\]/g;
        let match;
        while ((match = regex.exec(newBody)) !== null) {
            let chunkId;
            if (match[3]) {
                // If there's a range, use both numbers
                chunkId = [parseInt(match[2]), parseInt(match[3])];
            } else {
                // If there's only one number, use it as a single-element array
                chunkId = [parseInt(match[2])];
            }
            newBody = buildCitationMap(match[0], newBody, citations, chunks, chunkId, setResponseData);
        }
        return newBody.replace(/\*\*(?=[a-zA-Z])/g, '<b>').replace(/\*\*(?=\s|\W)/g, '</b>');
    };

    const subscribeToSSE = async (UNIQUE_ID, setResponseData, setResponseLoading, setDocumentsLoading, setProgress, progress, storeResponse, payload, setBotError) => {
        const rootUrl = backendEndpoint;
        const SERVER_ENDPOINT = `${rootUrl}/api/agents/intelligent-search/stream/${UNIQUE_ID}`;
        const newSource = new EventSource(SERVER_ENDPOINT, {
            withCredentials: false
        });

        newSource.addEventListener('newToken', (event) => {
            if (isNotSameId(event, UNIQUE_ID)) return;

            let text = processToken(event.data);

            if (!text) return;
            if (progress < 100 && text.length > 0) {
                setProgress(100)
                setResponseLoading(false)
            }
            setResponseLoading(false);
            setResponseData(data => ({
                ...data,
                response: !data.response ? text : buildInlineCitations(data.response, data.citations, data.chunks, setResponseData) + text
            }));
        });

        newSource.addEventListener('error', (event) => {
            if (isNotSameId(event, UNIQUE_ID)) return;
            setProgress(0)
            unsubscribeFromSSE(newSource);
            setResponseLoading(false);
            setDocumentsLoading(false);
            setBotError({ open: true, message: "There was an error during your search. Please try again later" });
            setTimeout(() => {
                setBotError({ open: false, message: "" });
            }, 3000);
        });

        newSource.addEventListener('citations', (event) => {
            if (isNotSameId(event, UNIQUE_ID)) return;
            setResponseData(data => ({
                ...data,
                citations: JSON.parse(event.data)
            }));
        });

        newSource.addEventListener('chunks', (event) => {
            if (isNotSameId(event, UNIQUE_ID)) return;
            setResponseData(data => ({
                ...data,
                chunks: JSON.parse(event.data)
            }));
        });

        newSource.addEventListener('documentsInfo', (event) => {
            if (isNotSameId(event, UNIQUE_ID)) return;
            setDocumentsLoading(false);
            setResponseData(data => ({
                ...data,
                documents: JSON.parse(event.data),
            }));
        });

        newSource.addEventListener('done', (event) => {
            if (isNotSameId(event, UNIQUE_ID)) return;
            setResponseLoading(false);
            unsubscribeFromSSE(newSource);
            setResponseData(data => {
                const updatedData = {
                    ...data,
                    response: buildInlineCitations(data.response, data.citations, data.chunks),
                    done: true,
                    question,
                    ...payload
                };
                storeResponse(updatedData)
                return updatedData;
            })
        });

        return newSource;
    }


    const AnswerBox = ({ responseData, relatedQuestions, children, actions, relatedImages }) => (
        <>
            <Box id='section-3'
                sx={{
                    border: 1,
                    borderColor: 'grey.200',
                    borderRadius: '0 10px 10px 10px',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <SectionBox
                        title="Answer"
                        IconComponent={PiTextAlignLeftLight}
                        actions>
                        <Typography
                            padding={4}
                            paddingTop={2}>
                            <AnswerRenderer

                                responseData={responseData} />
                        </Typography>
                    </SectionBox>
                </Box>
                {children}
                {actions && <ActionButtons />}

            </Box>

        </>
    );



    const ActionButtons = () => {
        const copyTextToClipboard = (elementId) => {
            const div = document.getElementById(elementId);
            if (div) {
                const range = document.createRange();
                range.selectNode(div);
                window.getSelection().removeAllRanges();
                window.getSelection().addRange(range);
                document.execCommand('copy');
                window.getSelection().removeAllRanges();

                setTimeout(() => {

                }, 3000);
            }
        };

        return (
            <Stack direction="row"
                spacing={0.5}
                sx={{ position: 'absolute', bottom: 0, right: 0, mb: -1, ml: 2 }}>
                <ButtonGroup>
                    <ActionButton
                        IconComponent={AiOutlineCopy}
                        // onClick={copyTextToClipboard('answer-renderer')}
                        TooltipTitle='Copy' />
                    {/* <ActionButton IconComponent={AiOutlineMail}
                        TooltipTitle='Mail'
                        onClick={mailTo} /> */}
                </ButtonGroup>
            </Stack>
        );
    };
    const ActionButton = ({ IconComponent, onClick, TooltipTitle }) => {
        const [icon, setIcon] = useState(IconComponent);
        const handleClick = () => {
            setIcon(<AiOutlineCheck size={15} color="black" />);
            setTimeout(() => {
                setIcon(IconComponent);
            }, 3000);
            if (onClick) onClick();
        };
        return (

            <Tooltip title={TooltipTitle}
                leaveDelay={1000}
                placement='bottom'
            >
                <Button
                    size="small"
                    onClick={handleClick} // Use the onClick prop
                    sx={{
                        backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100'),
                        borderRadius: '3px',
                        borderColor: 'neutral.200',
                        // '&:hover': {
                        //     backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'neutral.100' : 'neutral.300'),
                        // },
                    }}
                >
                    {icon}
                </Button>
            </Tooltip>
        );
    };

    const TypographySection = ({ title, variant, fontSize, mb, fontWeight }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Typography variant={variant}
                mb={mb}
                sx={{ fontSize }}
                fontWeight={fontWeight}>
                {title}
            </Typography>
        </Box>
    );

    const SectionBox = ({ title, IconComponent, children, actions }) => (
        <Box
            sx={{
                position: 'relative',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: 3, mt: 2 }}>
                <Grid container
                    alignItems="flex-start"
                    spacing={0.5}>
                    <Grid item
                        sx={{ pb: 0 }}>
                        <IconComponent size={24} />
                    </Grid>
                    <Grid item>
                        <Typography variant="body1"
                            sx={{ fontSize: '1.15rem' }}>
                            {title}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
            {children}
        </Box>
    );
    const SourcesBox = ({ responseData }) => (
        <Box id='section-2'
            sx={{
                border: 1,
                borderColor: 'grey.200',
                borderRadius: '0 10px 10px 10px',
                position: 'relative',
                mb: 2,
            }}
        >
            <SectionBox title="Sources"
                IconComponent={PiBook}>
                <Spacer height="1rem" />
                <SourcesList responseData={responseData} />
            </SectionBox>
        </Box>
    );

    const QuestionSection = ({ query }) => (
        <Stack id='section-1'>
            <TypographySection title={query}
                variant="body1"
                fontSize="1.7rem"
                fontWeight={500}
                mb={1} />
        </Stack>
    );


    const AnswerRenderer = ({ responseData, actions, responseLoading }) => {
        return (
            <Box id='section-3'
                sx={{
                    border: 'none',
                    position: 'relative',
                }}>
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <div id='answer-renderer' dangerouslySetInnerHTML={{ __html: convertToHTML(responseData?.response) }} />
                </Box>
                {/* {actions && responseLoading == false && <ActionButtons />} */}
            
            </Box>
        );
    };

    function convertToHTML(text) {
        if (!text) return '';
        return text.replace(/\n/g, '<br>');
    }


    useEffect(() => {
        if (props.asephaSettings) {

            const { asephaQuery } = props?.asephaSettings;
            if (asephaQuery) {
                handleSearch(asephaQuery, setResponseData, setResponseLoading, setDocumentsLoading, setProgress, progress, activeSource, setActiveSource, setStartTime, setResponseDataStoreValue, setBotError);
            }
        }
    }, [props.asephaSettings]);

    useEffect(() => {
        if (extension) {
            sendToContentScript({ name: 'getUrl' }).then((url) => {
                if (url) {
                    getResponseData().then((data) => {
                        if (data?.context_url === url) {
                            setResponseData(data)
                            setTempQuestion(data.question)
                            setQuestion('')
                            changeSearchType(data?.context_url)
                        }
                        else {
                            setQuestion('')
                        }
                    })
                }
            }).catch((error) => {
                return null;    
            })
        }
    }, []);

    useEffect(() => {
        if (responseData.done)
            setResponseDataStoreValue(responseData)
    }, [responseData.done]);

    // Empty dependency array ensures this runs only on the first load of the component


    useEffect(() => {
        const incrementProgress = () => {
            if (responseLoading) {
                const elapsedTime = (Date.now() - startTime) / 15000;
                const nextProgress = easeOutFunction(Math.min(elapsedTime, 1)) * 99
                setProgress(nextProgress);
                if (nextProgress >= 100) {
                    setResponseLoading(false);
                }

            }
        };

        const timer = setInterval(incrementProgress, 700);
        return () => clearInterval(timer);
    }, [progress, responseLoading]);


    return (
        <Box sx={{ padding: 0, pointerEvents: props.disableBot ? 'none' : 'auto', opacity: props.disableBot ? 0.7 : 1 }}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                open={botError?.open}
                message={botError.message}
                autoHideDuration={2000}
            />
            <Card>

                <CardContent sx={{ pl: 2, pr: 0, pt: extension ? 0 : '' }}>
                    <Stack spacing={2}>
                        {showSearchBar && <Stack spacing={2}>
                            <Grid container
                                spacing={2}
                                width="100%">
                                <Grid item
                                    xs={8}
                                    sm={10}
                                    lg={9}
                                    xl={10}
                                    md={9}
                                >

                                    <Card
                                        sx={{
                                            borderRadius: 1,
                                            justifyContent: 'space-between',
                                            mt: 1,
                                            boxShadow: 'none !important',
                                            border: 'none !important'
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            inputRef={inputRef}
                                            disabled={!!activeSource}
                                            label={asephaSettings?.asephaSearchBar?.title || 'Ask a question'}
                                            name="Question"
                                            id="userQuestion"
                                            value={question}
                                            onChange={(e) => {
                                                handleQuestionChange(e);
                                                highlightDrugs(e.target.value);
                                            }}
                                            onKeyUp={(e) => {
                                                if (e.key === 'Enter' && question.length > 1) {
                                                    if (responseLoading || documentsLoading || activeSource) unsubscribeFromSSE(activeSource, setActiveSource);
                                                    else handleSearch(question, setResponseData, setResponseLoading, setDocumentsLoading, setProgress, progress, activeSource, setActiveSource, setStartTime, setResponseDataStoreValue, setBotError)
                                                }
                                            }}
                                            InputLabelProps={extension ? { shrink: true } : null}
                                            InputProps={{
                                                endAdornment: (
                                                    <>
                                                        {extension && (
                                                            <Chip
                                                                sx={{ mt: '10px', mr: 1 }}
                                                                avatar={searchType.avatar}
                                                                size="small"
                                                                label={searchType.text}
                                                                onClick={changeSearchType}
                                                            />
                                                        )}
                                                        {/* {highlightedDrugs.map((drug, index) => (
                                                            <DrugBubble key={index} name={drug} />
                                                        ))} */}
                                                    </>
                                                ),
                                                disableUnderline: true,
                                                sx: {
                                                    minHeight: 59,
                                                    fontSize: 24,
                                                    fontWeight: 600,
                                                    '&:before': {
                                                        borderBottom: 'none',
                                                    },
                                                    '&:after': {
                                                        borderBottom: 'none',
                                                    },
                                                },
                                            }}
                                        />
                                    </Card>
                                    {disclaimerShow && !extension && <Typography
                                        sx={{
                                            mt: 1,
                                            mr: 2,
                                            pl: '2px',
                                            pr: 1,
                                            fontSize: '0.8rem',
                                            color: 'text.secondary',
                                            fontStyle: 'italic',
                                            width: '81vw'
                                        }}
                                    >
                                        {disclaimerText}
                                    </Typography>
                                    }
                                </Grid>

                                <Grid item
                                    xs={4}
                                    sm={2}
                                    lg={3}
                                    xl={2}
                                    md={3}

                                >
                                    <Card
                                        // elevation={0}
                                        onClick={() => {
                                            if (responseLoading || documentsLoading || activeSource) unsubscribeFromSSE(activeSource, setActiveSource);
                                            else handleSearch(question, setResponseData, setResponseLoading, setDocumentsLoading, setProgress, progress, activeSource, setActiveSource, setStartTime, setResponseDataStoreValue, setBotError)
                                        }}

                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRadius: 1,
                                            justifyContent: 'space-between',
                                            mt: 1,
                                            boxShadow: 'none; !important',
                                            border: 'none; !important'

                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: "primary.main",
                                            }}

                                        >
                                            <SvgIcon
                                                sx={{
                                                    height: 41,
                                                }}
                                            >
                                                {responseLoading || documentsLoading || activeSource ? <StopRounded /> : <SearchMdIcon />}
                                            </SvgIcon>
                                        </Button>
                                    </Card>
                                </Grid>
                            </Grid>

                            {asephaSettings && asephaSettings.size > 0 && asephaSettings?.asephaMode != 'full' &&
                                < IconButton sx={{ position: 'absolute', top: 0, right: '3%', p: 0 }}
                                    onClick={handleClose}
                                    aria-label="close">
                                    <CloseIcon />
                                </IconButton>
                            }


                        </Stack>
                        }

                        {
                            responseData && !UI_VERSION &&
                            <> {(responseLoading || responseData.documents.length > 0) &&
                                <div>
                                    < Typography
                                        color="text.secondary"
                                        variant="overline">
                                        Question
                                    </Typography>

                                    <div>{tempQuestion}</div>

                                </div>
                            }
                                <Stack>
                                    <Typography
                                        color="text.secondary"
                                        variant="overline">
                                    </Typography>
                                    {

                                        <MarkdownWrapper>
                                            <Markdown>
                                                {/* {answer} */}
                                            </Markdown>
                                        </MarkdownWrapper>
                                    }
                                    {
                                        showOverloadMessage &&
                                        <MarkdownWrapper>
                                            <Markdown>
                                                Service temporarily unavailable due to high demand. We apologize for the inconvenience. Please try again soon.
                                            </Markdown>
                                        </MarkdownWrapper>
                                    }
                                    {responseLoading && responseData.response == '' &&
                                        < Stack sx={{ width: '100%', color: 'grey.500' }}
                                            spacing={2}>
                                            <LinearProgressWithLabel value={progress}
                                                color="primary"
                                                sx={{ height: 15 }} />
                                            <LinearProgress variant="determinate"
                                                value={progress}
                                                color="primary"
                                                sx={{ height: 15, width: '95.6%' }} />
                                            <LinearProgress variant="determinate"
                                                value={progress}
                                                color="primary"
                                                sx={{ height: 15, width: '95.6%' }} />
                                        </Stack>
                                    }
                                    {
                                        !responseLoading && responseData.response.length > 0 &&
                                        < div >
                                            <Typography
                                                color="text.secondary"
                                                variant="overline"
                                            >
                                                Answer
                                            </Typography>
                                            <AnswerRenderer
                                                responseData={responseData}
                                                actions
                                                responseLoading={responseLoading} />
                                        </div>
                                    }
                                </Stack>
                                <Stack spacing={1}>
                                    {
                                        responseData.documents.length > 0 &&
                                        <div>
                                            <Typography
                                                color="text.secondary"
                                                variant="overline"
                                            >
                                                References
                                            </Typography>
                                            <References resources={responseData?.documents}
                                                currentResource={''}
                                                extension={extension} />
                                        </div>
                                    }
                                    {
                                        documentsLoading && responseData.documents.length == 0 &&
                                        <Stack sx={{ width: '100%', color: 'grey.500' }}
                                            spacing={2}>
                                            <LinearProgress variant="determinate"
                                                value={progress}
                                                sx={{ height: 15, width: '95.6%' }} />
                                            <LinearProgress variant="determinate"
                                                value={progress}
                                                sx={{ height: 15, width: '95.6%' }} />
                                            <LinearProgress variant="determinate"
                                                value={progress}
                                                sx={{ height: 15, width: '95.6%' }} />
                                        </Stack>
                                    }
                                    {
                                        showOverloadMessage &&
                                        <Typography>You can contact us at <a href="mailto: info@asepha.ai">info@asepha.ai</a></Typography>
                                    }
                                </Stack>
                            </>
                        }

                        {
                            responseData && UI_VERSION == 2 &&
                            <>


                                {responseData.documents.length > 0 && <QuestionSection query={tempQuestion}
                                    sources={null} />}
                                {responseData.documents.length > 0 && <SourcesBox responseData={responseData} />}
                                {responseData.response.length > 0 && <AnswerBox responseData={responseData} actions responseLoading={responseLoading} />}
                            </>
                        }
                        {
                            responseLoading && UI_VERSION == 2 && responseData.documents.length == 0 &&
                            <>
                                < Stack sx={{ width: '100%', color: 'grey.500' }}
                                    spacing={2}>
                                    <LinearProgressWithLabel value={progress}
                                        color="primary"
                                        sx={{ height: 15 }} />
                                    <LinearProgress variant="determinate"
                                        value={progress}
                                        color="primary"
                                        sx={{ height: 15, width: '95.6%' }} />
                                    <LinearProgress variant="determinate"
                                        value={progress}
                                        color="primary"
                                        sx={{ height: 15, width: '95.6%' }} />
                                </Stack>
                                {
                                    documentsLoading && responseData.documents.length == 0 &&
                                    <Stack sx={{ width: '100%', color: 'grey.500' }}
                                        spacing={2}>
                                        <LinearProgress variant="determinate"
                                            value={progress}
                                            sx={{ height: 15, width: '95.6%' }} />
                                        <LinearProgress variant="determinate"
                                            value={progress}
                                            sx={{ height: 15, width: '95.6%' }} />
                                        <LinearProgress variant="determinate"
                                            value={progress}
                                            sx={{ height: 15, width: '95.6%' }} />
                                    </Stack>
                                }

                            </>
                        }
                    </Stack>

                </CardContent>
            </Card>
        </Box >
    );
};





